/**
 * Renders ONE widget node by delegating to the existing shared component for
 * its `layoutType` — the same mapping the DVM Layout Builder's `tileBody`
 * template uses, so a KPI looks identical in both builders.
 *
 * **Rule:** the Design Builder never hand-rolls a widget's markup. To support a
 * new component type, add a branch to the template and an entry to
 * {@link WIDGET_LABELS} — nothing else.
 *
 * Two render modes:
 * - `placeholder` (icon + label) while the node is being dragged or resized, or
 *   before any data is bound. Keeps ECharts out of the drag loop entirely.
 * - the real component once the gesture ends and `data` exists.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

import { DrillDownComponent } from '../../../chart-components/drill-down/drill-down.component';
import { HorizontalBarChartComponent } from '../../../chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { UnifiedBarColumnChartComponent } from '../../../chart-components/unified-bar-column-chart/unified-bar-column-chart.component';
import { UnifiedLineAreaChartComponent } from '../../../chart-components/unified-line-area-chart/unified-line-area-chart.component';
import { UnifiedScatterBubbleChartComponent } from '../../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.component';
import { UnifiedPieCompositionChartComponent } from '../../../chart-components/unified-pie-composition-chart/unified-pie-composition-chart.component';
import { UnifiedTableMatrixChartComponent } from '../../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.component';
import type { TableMatrixCellButtonEvent, TableMatrixCellEditEvent } from '../../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { WaterfallChartComponent } from '../../../chart-components/waterfall-chart/waterfall-chart.component';
import { DvmFileUploadComponent } from '../../../chart-components/dvm-file-upload/dvm-file-upload.component';
import { DvmPopupComponent } from '../../../chart-components/dvm-popup/dvm-popup.component';
import { DvmActionButtonComponent } from '../../../global-components/dvm-action-button/dvm-action-button.component';
import { GlobalDropdownComponent } from '../../../global-components/global-dropdown/global-dropdown.component';
import { InputBoxComponent } from '../../../global-components/input-box/input-box.component';
import { MatSliderComponent } from '../../../global-components/mat-slider/mat-slider.component';
import { NumberStatsComponent } from '../../../global-components/number-stats/number-stats.component';
import { ZoneSliderComponent } from '../../../global-components/zone-slider/zone-slider.component';
import { ZONE_SLIDER_DEFAULTS, finiteOrNull } from '../../../global-components/zone-slider/zone-slider.utils';
import type { DisplayCurrency } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { NestedTableComponent } from '../../components/nested-table/nested-table.component';
import { inputSaveConfigOf, updateRequestBody } from '../utils/design-save-request.utils';
import type { NestedTableActionEvent, NestedTableCellButtonEvent, NestedTableCellChange, NestedTableLevel, NestedTableRowSaveEvent } from '../../components/nested-table/nested-table.types';
import { DesignRuntimeService, exportColumnsOf, nestedColumnAliases } from '../data/design-runtime.service';
import { tileDataset } from '../data/design-tiles.utils';

import { DesignDataService } from '../services/design-data.service';

import { WIDGET_PALETTE_META } from '../design-builder.constants';
import type { WidgetContent } from '../design-builder.types';
import { resolveWidgetData } from './design-widget-data.utils';
import { isLiveOnlyWidget, withoutStoredRows } from '../utils/design-publish.utils';
import { withInputValue } from '../utils/design-input-value.utils';
import { VALUE_FORMULA_KEY, evaluateExpression } from '../utils/design-formula.utils';
import { applyZoneSliderBinding, zoneSliderBindingOf, zoneSliderDatasets, zoneSliderStaticData } from './zone-slider-binding.utils';

/** Friendly names for the placeholder, keyed by layoutType. */
export const WIDGET_LABELS: Record<string, string> = {
  WATERFALL: 'Waterfall',
  DRILL_DOWN_TABLE: 'Drill-down table',
  TABLE_MATRIX: 'Table & matrix',
  NESTED_TABLE: 'Nested table',
  BAR: 'Bar chart',
  LINE_AREA: 'Line & area',
  SCATTER_BUBBLE: 'Scatter & bubble',
  PIE_COMPOSITION: 'Pie & composition',
  HORIZONTAL_BAR_CHART: 'Horizontal bar',
  KPI: 'KPI card',
  INPUT_BOX: 'Input box',
  SLIDER: 'Slider',
  ZONE_SLIDER: 'Zone slider',
  DROPDOWN: 'Dropdown',
  FILE_UPLOAD: 'File upload',
  POPUP: 'Popup',
  BUTTON: 'Button',
};

@Component({
  selector: 'app-design-widget-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    DrillDownComponent,
    HorizontalBarChartComponent,
    UnifiedBarColumnChartComponent,
    UnifiedLineAreaChartComponent,
    UnifiedScatterBubbleChartComponent,
    UnifiedPieCompositionChartComponent,
    UnifiedTableMatrixChartComponent,
    WaterfallChartComponent,
    DvmFileUploadComponent,
    DvmPopupComponent,
    DvmActionButtonComponent,
    GlobalDropdownComponent,
    InputBoxComponent,
    MatSliderComponent,
    ZoneSliderComponent,
    NumberStatsComponent,
    NestedTableComponent,
  ],
  templateUrl: './design-widget-host.component.html',
  styleUrl: './design-widget-host.component.scss',
})
export class DesignWidgetHostComponent {
  readonly widget = input.required<WidgetContent>();
  readonly nodeId = input<string>('');
  /** Dashboard interactions (absent in thumbnails, which are not interactive). */
  protected readonly runtime = inject(DesignRuntimeService, { optional: true });
  /** Node name — shown on the placeholder and used as a fallback title. */
  readonly title = input<string>('');
  /**
   * True while the node is mid-drag/resize. The real component is swapped for a
   * lightweight placeholder so ECharts is not re-laid-out on every pointermove.
   */
  readonly suspended = input<boolean>(false);
  /**
   * Data-connection mode: hide generated sample charts behind the placeholder.
   *
   * While designing, sample data is the point — you judge the layout against
   * something that looks real. While CONNECTING data it works against you: a
   * convincing fake is indistinguishable from a bound chart, so you cannot see
   * what is still outstanding.
   */
  readonly hideSample = input<boolean>(false);

  readonly layoutType = computed(() => this.widget().layoutType);

  /**
   * The widget's data with the DESIGN TIER overlaid — this is what makes the
   * inspector's variant/format choices actually show up on the chart. Without
   * the overlay the chart would render only what the sample generator produced
   * and every format control would look broken.
   */
  readonly data = computed(() => {
    const widget = this.widget();
    const live = this.runtime?.dataFor(this.nodeId());
    if (live) return resolveWidgetData({ ...widget, data: live, sample: false });
    // A nested table shows only live compute-API rows - never ones stored with the design.
    return resolveWidgetData(isLiveOnlyWidget(widget) ? { ...widget, data: withoutStoredRows(widget.data) } : widget);
  });

  /** Current value of a generated input control (dropdown / slider / input box). */
  readonly controlValue = computed(() => {
    const tile = this.runtime?.tileFor(this.nodeId());
    return tile?.dropdownControl?.value ?? tile?.inputControl?.value ?? this.runtime?.localDropdownValues()[this.nodeId()] ?? null;
  });

  /**
   * A filter / input change is recomputing this widget. Filters address their
   * targets by the WIDGET's componentId, which the build copy may not carry,
   * so both are checked.
   */
  readonly isRecomputing = computed(() => {
    const busy = this.runtime?.busy();
    if (!busy?.size) return false;
    const ids = [this.widget().componentId, this.runtime?.tileFor(this.nodeId())?.componentId];
    return ids.some((id) => !!id && busy.has(String(id)));
  });

  readonly uploadMessage = computed(() => this.runtime?.uploadMessages()[this.nodeId()] ?? '');

  onCrossFilter(event: { column: string; value: string | null; values?: string[] }): void {
    this.runtime?.onCrossFilter(this.nodeId(), event);
  }

  /** Nothing to render yet, or deliberately suspended → show the placeholder. */
  /** True while this widget is showing generated sample data. */
  readonly isSample = computed(() => this.widget().sample === true);

  readonly showPlaceholder = computed(() => this.suspended() || !this.data());

  /** The compute answered no rows for the active filters: say so, rather than draw stored / earlier data. */
  readonly showNoData = computed(() => !this.suspended() && !!this.runtime?.noData().has(this.nodeId()));

  /** First data fetch still pending → loading state rather than an empty chart. */
  readonly showLoading = computed(() => {
    if (this.suspended()) return false;
    if (this.runtime?.pending().has(this.nodeId())) return true;
    // The zone slider reads its value / range / zones from dataset rows.
    if (!this.isZoneSlider() || !this.dataService) return false;
    return zoneSliderDatasets(this.zoneSliderBinding()).some(
      (dataset) => this.dataService!.rows(dataset).status === 'loading',
    );
  });

  readonly label = computed(
    () => WIDGET_LABELS[this.layoutType()] ?? this.layoutType() ?? 'Component',
  );

  readonly icon = computed(() => WIDGET_PALETTE_META[this.layoutType()]?.icon ?? 'widgets');

  /** Form controls render at their natural height; charts fill the node. */
  readonly isInputControl = computed(
    () => WIDGET_PALETTE_META[this.layoutType()]?.group === 'inputs',
  );

  // ── Dropdown field height (Design tab: "Field height" / "Field fills box height")
  /** Exact field height: the dropdown sizes itself from this var (default 36px). */
  readonly fieldHeightVar = computed<string | null>(() => {
    if (this.layoutType() !== 'DROPDOWN') return null;
    const size = Number(this.widget().appearance?.['fieldHeight']);
    return Number.isFinite(size) && size > 0 ? `${size}px` : null;
  });

  /** Follow the layer's resize handles unless a fixed-height field was requested. */
  readonly fieldFillHeight = computed(() =>
    this.layoutType() === 'DROPDOWN' && this.widget().appearance?.['fieldFillHeight'] !== false);

  /** Widgets whose shared component expects a non-null `[data]` object. */
  readonly widgetData = computed<Record<string, unknown>>(() => this.withSaveButton(this.withCanvasFilters(this.withCurrency(this.withFormulaValue(this.withDataValue(this.data() ?? {}))))));

  /** An input box with "Save button (update)" on: the button, and how its last save went. */
  private withSaveButton(data: Record<string, unknown>): Record<string, unknown> {
    if (this.layoutType() !== 'INPUT_BOX') return data;
    const save = inputSaveConfigOf(this.widget().binding as Record<string, unknown> | undefined);
    if (!save) return data;
    const state = this.runtime?.buttonStates()[`${this.nodeId()}:save`];
    return { ...data, saveButton: { label: save.label?.trim() || 'Save' },
      saveState: state ? { busy: state.busy, kind: state.kind, message: state.message } : null };
  }

  /** The input box's Save: its current value goes to the Save API. */
  onInputSave(value: string): void {
    this.runtime?.saveInputValue(this.nodeId(), value);
  }

  /** A nested table loads its child levels itself: they carry the canvas filters that target it. */
  private withCanvasFilters(data: Record<string, unknown>): Record<string, unknown> {
    if (this.layoutType() !== 'NESTED_TABLE') return data;
    const canvasFilters = this.runtime?.canvasFiltersForNode(this.nodeId()) ?? [];
    return canvasFilters.length ? { ...data, canvasFilters } : data;
  }

  // ── Currency simulation ──────────────────────────────────────────────────

  /** Tables get the picked currency; their "Convert currency" columns multiply by its rate. */
  private withCurrency(data: Record<string, unknown>): Record<string, unknown> {
    const type = this.layoutType();
    // Tables: their columns ticked "Convert currency" follow the exchange rate.
    if (type === 'NESTED_TABLE' || type === 'TABLE_MATRIX') {
      const currency = this.runtime?.currencyForTable(this.nodeId()) ?? null;
      if (!currency) return data;
      if (type === 'NESTED_TABLE') return { ...data, currency };
      const capabilities = (data['capabilities'] ?? {}) as Record<string, unknown>;
      return { ...data, capabilities: { ...capabilities, currency } };
    }
    // A KPI card the exchange dropdown targets: its single value × the rate.
    const currency = this.runtime?.currencyFor(this.nodeId()) ?? null;
    if (currency && type === 'KPI') return kpiTimesRate(data, currency.rate);
    return data;
  }

  /**
   * An input box filled from data shows the column's value for the current
   * filters, or what the user typed over it since.
   */
  private withDataValue(data: Record<string, unknown>): Record<string, unknown> {
    const type = this.layoutType();
    if ((type !== 'INPUT_BOX' && type !== 'SLIDER') || !this.runtime) return data;
    // What the user typed / dragged wins - for EVERY input, not only "Fill from
    // data" ones: a plain input re-rendered from its saved value (0.0) right
    // after Enter, because the edit re-builds the widget's data without it.
    const typed = this.runtime.inputValues()[this.nodeId()];
    if (typed !== undefined) return withInputValue(type, data, typed);
    if (type !== 'INPUT_BOX') return data;
    const fromData = this.runtime.inputDataValues()[this.nodeId()];
    if (!fromData) return data;
    if (fromData.status === 'ready' && fromData.value !== null) return withInputValue('INPUT_BOX', data, fromData.value);
    // The column is null: show "-", not the saved default (0.0).
    if (fromData.status === 'ready') return { ...data, value: null };
    if (fromData.status === 'loading') return { ...data, placeholder: 'Loading…' };
    return data;
  }

  // ── Value formula (input box / slider / zone slider) ───────────────────────

  /** e.g. `first([Future Volumes], [Current Volumes])` — see `design-formula.utils`. */
  private readonly valueFormula = computed(() => {
    const formula = this.widget().appearance?.[VALUE_FORMULA_KEY];
    return typeof formula === 'string' ? formula : '';
  });

  private readonly formulaResult = computed(() => {
    const expression = this.valueFormula();
    return expression.trim() && this.dataService ? this.dataService.formula({ expression }, this.nodeId()) : null;
  });

  /**
   * A value the user set by hand on a formula-driven input. It holds until
   * something ELSE changes: another input is edited, or the filters pick another
   * record - then the formula takes over again. Values the dashboard recomputes
   * BECAUSE of this edit (texts / charts it targets) do not count: comparing the
   * formula's result instead dropped the typed value right after Enter.
   */
  private readonly manual = signal<{ value: number | string; basis: string } | null>(null);

  /** What a hand-set value is held against: every other input's value and the active filters. */
  private readonly manualBasis = computed(() => {
    const inputs = { ...(this.runtime?.inputValues() ?? {}) };
    delete inputs[this.nodeId()];
    const filters = (this.runtime?.activeFilters() ?? []).map((filter) => [filter.field, filter.values]);
    return JSON.stringify([inputs, filters]);
  });

  /** The formula's value (or the user's override of it) laid over the widget's data. */
  private withFormulaValue(data: Record<string, unknown>): Record<string, unknown> {
    const result = this.formulaResult();
    if (result?.status !== 'ready') return data;
    const manual = this.manual();
    const value = manual && manual.basis === this.manualBasis() ? manual.value : result.value;
    return withInputValue(this.layoutType(), data, value);
  }

  /** Every input's user change: remember it against the formula, then run the input as usual. */
  onUserValue(value: number | string): void {
    if (this.valueFormula().trim()) this.manual.set({ value, basis: this.manualBasis() });
    this.runtime?.onInputValueChange(this.nodeId(), value);
  }

  /**
   * A nested level's button: Export is done by the table itself; navigate / API
   * run through the runtime like a Button widget, with `{rows}` (that table's
   * rows) and `{parentRow}` (the row it is expanded under) as body tokens.
   */
  onNestedAction(event: NestedTableActionEvent): void {
    const levels = (this.widgetData()['levels'] ?? []) as NestedTableLevel[];
    const button = levels[event.levelIndex]?.actions?.find((item) => item.id === event.actionId);
    if (!button || !button.action || button.action === 'export') return;
    if (button.action === 'excel') {
      const tile = this.runtime?.tileFor(this.nodeId());
      const entity = tile ? tileDataset(tile) : '';
      const flat = (row: Record<string, unknown>) =>
        Object.fromEntries(Object.entries(row).filter(([, value]) => !Array.isArray(value)));
      this.runtime?.exportExcel(`${this.nodeId()}:${button.id}`, {
        entity,
        fileName: entity,
        sheetName: button.sheetName,
        url: button.apiUrl,
        bodyTemplate: button.apiBody,
        filterColumns: button.filterColumns,
        // Only this level's configured columns are exported.
        columns: exportColumnsOf(this.widgetData(), entity, event.levelIndex),
        contextRows: (event.ancestors ?? (event.parentRow ? [event.parentRow] : [])).map(flat),
        rows: event.rows.map(flat),
        aliases: nestedColumnAliases(this.widgetData()),
      });
      return;
    }
    const strip = (row: unknown) => {
      if (!row || typeof row !== 'object') return row ?? null;
      return Object.fromEntries(Object.entries(row as Record<string, unknown>).filter(([, value]) => !Array.isArray(value)));
    };
    const raw: Record<string, unknown> = button.action === 'navigate'
      ? { action: 'navigate', navigateType: button.navigateType || 'url', navigateTarget: button.navigateTarget ?? '', navigateNewTab: !!button.navigateNewTab }
      : {
          action: 'api',
          apis: [{ id: button.id, name: button.label, method: button.apiMethod || 'POST', url: button.apiUrl ?? '', body: button.apiBody ?? '', headers: '' }],
          apiSuccessMessage: 'Done',
        };
    this.runtime?.runTableButton(`${this.nodeId()}:${button.id}`, raw, {
      level: event.levelIndex + 1,
      rows: event.rows.map(strip),
      parentRow: strip(event.parentRow),
    });
  }

  /**
   * A nested row's Save — the update request. Everything is fixed except
   * `requestData` (the business keys with their values, plus the row's input /
   * dropdown values) and the `entity`.
   */
  onNestedRowSave(event: NestedTableRowSaveEvent): void {
    if (!this.runtime || !event.save.url) {
      event.done(false);
      return;
    }
    const row = event.row as Record<string, unknown>;
    // Level-wise: the edited row first, then the rows it sits under (nearest first).
    const sources = [row, ...[...event.ancestors].reverse()] as Array<Record<string, unknown>>;
    const keyValue = (key: string) => {
      const holder = sources.find((source) => source[key] !== undefined && source[key] !== null && source[key] !== '');
      return holder ? holder[key] : null;
    };
    // Only the columns the user edited in this row — of those chosen to send
    // (none chosen = every edited one). Edit one, one goes; edit n, n go.
    const allowed = (event.save.payloadColumns ?? []).filter(Boolean);
    const values: Record<string, unknown> = {};
    event.editedFields
      .filter((field) => !allowed.length || allowed.includes(field))
      .forEach((field) => (values[field] = row[field] ?? null));
    const entity = event.save.entity?.trim() || String(this.widget().binding?.['dataset'] ?? '');
    const body = updateRequestBody(entity, event.save.businessKeys ?? [], keyValue, values);
    this.runtime.postTableSave$(event.save.url, body).subscribe((ok) => event.done(ok));
  }

  /** A typed / picked table cell — kept by the runtime for action buttons ({tableEdits}). */
  onNestedCellChange(change: NestedTableCellChange): void {
    const { children: _children, ...row } = change.row as Record<string, unknown>;
    this.runtime?.recordTableEdit(this.nodeId(), {
      level: change.levelIndex + 1,
      path: change.path,
      field: change.field,
      value: change.value,
      // Child rows are dropped: the edited row's own values identify it.
      row: Object.fromEntries(Object.entries(row).filter(([, value]) => !Array.isArray(value))),
    });
  }

  /** The last cell button clicked in this table, by its state id. */
  private readonly lastCellButton = signal('');
  /** That button's busy / result message, while there is one. */
  readonly cellButtonNote = computed(() => {
    const key = this.lastCellButton();
    const state = key ? this.runtime?.buttonStates()[key] : undefined;
    return state && (state.busy || state.message) ? state : null;
  });

  /** A button in a Normal / Editable table cell: run its feature for the clicked row. */
  onTableCellButton(event: TableMatrixCellButtonEvent): void {
    const stateId = `${this.nodeId()}:${event.field}:${event.button.id}`;
    this.lastCellButton.set(stateId);
    this.runtime?.runCellButton(stateId, event.button.config, event.row);
  }

  /** A button in a nested table cell: run its feature for the row, with the rows above it. */
  onNestedCellButton(event: NestedTableCellButtonEvent): void {
    const stateId = `${this.nodeId()}:${event.levelIndex}:${event.field}:${event.button.id}`;
    this.lastCellButton.set(stateId);
    this.runtime?.runCellButton(stateId, event.button.config, event.row, event.ancestors);
  }

  /** A cell changed in a Normal / Editable table — kept like nested-table edits (Approval, {tableEdits}). */
  onTableCellEdit(edit: TableMatrixCellEditEvent): void {
    this.runtime?.recordTableEdit(this.nodeId(), {
      level: 1,
      path: [],
      rowKey: edit.rowId,
      field: edit.field,
      value: edit.value,
      row: edit.row,
    });
  }

  onSliderUserValue(range: { start: number; end: number }): void {
    if (this.valueFormula().trim() && Number.isFinite(range?.end)) this.manual.set({ value: range.end, basis: this.manualBasis() });
    this.runtime?.onSliderChange(this.nodeId(), range);
  }

  /**
   * `app-global-dropdown` reads its choices from the `[options]` input (a plain
   * `string[]`), and only falls back to fetching when `data.apiUrl` is set. The
   * sample payload carries them under `options`, so they are forwarded here —
   * without this the control renders as an empty dropdown.
   */
  readonly dropdownOptions = computed<string[]>(() => {
    const options = this.widgetData()['options'];
    if (!Array.isArray(options)) return [];
    return options.map((option) =>
      typeof option === 'string' ? option : String((option as { value?: unknown })?.value ?? ''),
    );
  });

  // `app-dvm-file-upload` takes discrete @Inputs instead of a [data] bag, so
  // its two settings are pulled out of the same payload here.
  readonly uploadLabel = computed(() => String(this.widgetData()['buttonLabel'] ?? 'Upload file'));
  readonly uploadAccept = computed(() => String(this.widgetData()['accept'] ?? '.xlsx,.xls,.csv'));
  readonly uploadButtonStyle = computed<Record<string, string>>(() => {
    const appearance = this.widget().appearance ?? {};
    const out: Record<string, string> = {};
    for (const [key, css] of Object.entries({ uploadBackground: 'background', uploadTextColor: 'color', uploadBorderColor: 'border-color', uploadFontFamily: 'font-family' })) {
      const value = appearance[key];
      if (typeof value === 'string' && value) out[css] = value;
    }
    for (const [key, css] of Object.entries({ uploadFontSize: 'font-size', uploadBorderRadius: 'border-radius', uploadBorderWidth: 'border-width', uploadPaddingX: 'padding-inline', uploadPaddingY: 'padding-block' })) {
      const value = appearance[key];
      if (value !== undefined && value !== '' && Number.isFinite(Number(value))) out[css] = `${Math.max(0, Number(value))}px`;
    }
    return out;
  });


  // ── Zone slider: data tier resolved from dataset rows ──────────────────────
  // Optional: thumbnails render outside the builder with no data service and
  // simply show the static configuration.
  private readonly dataService = inject(DesignDataService, { optional: true });

  readonly isZoneSlider = computed(() => this.layoutType() === 'ZONE_SLIDER');

  private readonly zoneSliderBinding = computed(() => zoneSliderBindingOf(this.widget().binding));

  /**
   * Static config (every appearance key) with the compute API's value / min /
   * max / cost (once Generate has run) and the dataset's zone rows on top.
   */
  /** The slider in its own (base) currency - what approval levels and formulas compare against. */
  private readonly zoneSliderBaseData = computed<Record<string, unknown>>(() => {
    if (!this.isZoneSlider()) return this.widgetData();
    const compute = this.runtime?.zoneValues()[this.nodeId()];
    return this.withMarginFormula(this.withFormulaValue(applyZoneSliderBinding(
      zoneSliderStaticData(this.widget()),
      this.zoneSliderBinding(),
      (dataset) => {
        const state = this.dataService?.rows(dataset);
        return state?.status === 'ready' ? state.value : null;
      },
      compute?.status === 'ready' ? compute.values : null,
    )));
  });

  /**
   * Margin formula (Design tab → Sub label) → `marginFn`, re-run by the slider
   * on every drag. `[Value]` / `[Handle]` = the handle, `[Cost]` `[Min]` `[Max]`
   * `[Margin Column]` = this slider's numbers; any other `[Name]` is a layer.
   */
  private withMarginFormula(data: Record<string, unknown>): Record<string, unknown> {
    const fromDesign = typeof data['marginFormula'] === 'string' ? data['marginFormula'].trim() : '';
    // A leading "margin =" label (Excel-style "=" too) is not part of the maths.
    const formula = (this.zoneSliderBinding().marginFormula.trim() || fromDesign).replace(/^\s*(?:[A-Za-z_][\w ]*)?=(?!=)\s*/, '');
    if (!formula) return data;
    const keyOf = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');
    const column = typeof data['margin'] === 'number' ? data['margin'] : null;
    const binding = this.zoneSliderBinding();
    // Dataset columns named in the formula: the handle's column follows the drag, the rest are fetched values.
    const columnValue = (col: string) => (typeof data[`col:${col}`] === 'number' ? data[`col:${col}`] as number : null);
    const marginFn = (ctx: { value: number; min: number; max: number; cost: number | null }): number | null => {
      const own: Record<string, number | null> = {
        value: ctx.value, handle: ctx.value, cost: ctx.cost, min: ctx.min, max: ctx.max, margincolumn: column,
      };
      binding.marginColumns.forEach((col) => { own[keyOf(col)] = col === binding.valueField ? ctx.value : columnValue(col); });
      if (binding.valueField) own[keyOf(binding.valueField)] = ctx.value;
      const result = evaluateExpression(formula, (name) => {
        const key = keyOf(name);
        if (key in own) return own[key];
        const layer = this.dataService?.formula({ expression: `[${name}]` }, this.nodeId());
        return layer?.status === 'ready' ? layer.value : null;
      });
      return result.status === 'ready' ? result.value : null;
    };
    return { ...data, marginFn };
  }

  /**
   * The currency the slider is shown in: the "Currency exchange" dropdown that
   * targets it, else the dashboard's picked one - but only for a money slider
   * (prefix is a currency sign; a % slider must not be multiplied by a rate).
   */
  private readonly zoneSliderCurrency = computed<DisplayCurrency | null>(() => {
    if (!this.isZoneSlider() || !this.runtime) return null;
    const targeted = this.runtime.currencyFor(this.nodeId());
    const currency = targeted ?? this.runtime.activeCurrency();
    if (!currency || !Number.isFinite(currency.rate) || currency.rate <= 0) return null;
    if (targeted) return currency;
    const prefix = this.zoneSliderBaseData()['prefix'];
    return isCurrencySign(typeof prefix === 'string' ? prefix : ZONE_SLIDER_DEFAULTS.prefix) ? currency : null;
  });

  readonly zoneSliderData = computed<Record<string, unknown>>(() => {
    const data = this.zoneSliderBaseData();
    const currency = this.zoneSliderCurrency();
    return currency ? zoneSliderInCurrency(data, currency) : data;
  });

  constructor() {
    // Rows for the bound datasets — idempotent, same request a bound text makes.
    effect(() => {
      if (!this.isZoneSlider() || !this.dataService) return;
      zoneSliderDatasets(this.zoneSliderBinding()).forEach((dataset) => this.dataService!.loadRows(dataset));
    });
    // The runtime's level (popup message, "Text by level", "Show at levels")
    // reads what this slider draws, not a rebuild without its dataset rows.
    effect(() => {
      if (!this.isZoneSlider() || !this.runtime) return;
      this.runtime.setZoneSliderShown(this.nodeId(), this.zoneSliderBaseData());
    });
    // What an input box shows (formula, "Fill from data", typed) - a "Save changes" button sends exactly that.
    effect(() => {
      if (this.layoutType() !== 'INPUT_BOX' || !this.runtime) return;
      this.runtime.setShownValue(this.nodeId(), this.widgetData()['value']);
    });
  }

  // ── Button ─────────────────────────────────────────────────────────────────

  readonly isButton = computed(() => this.layoutType() === 'BUTTON');

  /** Spinner / result line / filter-applied state of this button. */
  readonly buttonState = computed(() => (this.isButton() ? this.runtime?.buttonStates()[this.nodeId()] : undefined));

  /** Content of the Popup widget a popup button shows. */
  readonly buttonPopupData = computed<Record<string, unknown> | null>(() => {
    if (!this.isButton()) return null;
    const appearance = this.widget().appearance ?? {};
    const popupId = appearance['popupSource'] === 'widget' ? String(appearance['popupWidgetId'] ?? '') : '';
    return popupId ? this.runtime?.widgetDataFor(popupId) ?? null : null;
  });

  /** Released value → the runtime, where a generated input would drive its targets. */
  onZoneSliderChange(value: number): void {
    // Shown converted: hand the runtime the base-currency value its levels use.
    const rate = this.zoneSliderCurrency()?.rate ?? 1;
    this.onUserValue(rate === 1 ? value : value / rate);
  }
}

/** A prefix that names money: a currency sign or a 3-letter code ("$", "₹", "USD"). */
function isCurrencySign(prefix: string): boolean {
  const text = prefix.trim();
  return /^[\p{Sc}]+$/u.test(text) || /^[A-Z]{3}\s*$/.test(text);
}

/**
 * The zone slider's numbers × the exchange rate, its prefix the picked
 * currency's sign: value, range, step, cost basis and every level boundary,
 * so the handle sits in the same level whatever the currency.
 */
function zoneSliderInCurrency(data: Record<string, unknown>, currency: DisplayCurrency): Record<string, unknown> {
  const d = ZONE_SLIDER_DEFAULTS;
  const rate = currency.rate;
  const times = (value: unknown) => {
    const number = finiteOrNull(value);
    return number === null ? value : number * rate;
  };
  // The stock example range when none was given (the slider fills it in itself).
  const stock = data['min'] === undefined && data['max'] === undefined;
  const zones = Array.isArray(data['zones']) ? data['zones'] : d.zones;
  // The margin formula mixes the handle with base-currency column / layer
  // values: hand it the handle and range back in the base currency.
  type MarginContext = { value: number; min: number; max: number; cost: number | null };
  const marginFn = data['marginFn'] as ((ctx: MarginContext) => number | null) | undefined;
  return {
    ...data,
    ...(typeof marginFn === 'function'
      ? { marginFn: (ctx: MarginContext) => marginFn({
          value: ctx.value / rate, min: ctx.min / rate, max: ctx.max / rate, cost: ctx.cost === null ? null : ctx.cost / rate,
        }) }
      : {}),
    value: times(data['value'] ?? (stock ? d.value : undefined)),
    min: times(data['min'] ?? (stock ? d.min : undefined)),
    max: times(data['max'] ?? (stock ? d.max : undefined)),
    step: times(data['step'] ?? d.step),
    cost: data['cost'] === null ? null : times(data['cost'] ?? d.cost),
    zones: zones.map((zone) => (zone && typeof zone === 'object'
      ? { ...(zone as Record<string, unknown>), from: times((zone as Record<string, unknown>)['from']), to: times((zone as Record<string, unknown>)['to']) }
      : zone)),
    ...(currency.symbol ? { prefix: currency.symbol } : {}),
  };
}

/** A KPI card's `data.data[*].value` × rate (the number-stats shape); anything else is left as is. */
function kpiTimesRate(data: Record<string, unknown>, rate: number): Record<string, unknown> {
  const inner = data['data'] as { data?: unknown } | undefined;
  const rows = inner?.data;
  if (!Array.isArray(rows)) return data;
  const times = (value: unknown) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    return value !== null && value !== '' && Number.isFinite(numeric) ? numeric * rate : value;
  };
  return { ...data, data: { ...inner, data: rows.map((row) => (row && typeof row === 'object'
    ? { ...(row as Record<string, unknown>), value: times((row as Record<string, unknown>)['value']) }
    : row)) } };
}
